'use client';

import React, { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 180000, suffix: '+', label: 'Vehicles Serviced', color: '#c60000' },
  { value: 15, suffix: '+', label: 'Trained Technicians', color: '#4db6ff' },
  { value: 25, suffix: '+', label: 'Service Categories', color: '#c60000' },
  { value: 30, suffix: '+ yrs', label: 'Experience in Addis Ababa', color: '#4db6ff' },
];

function useCountUp(target: number, active: boolean, duration = 1800) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const startTime = performance.now();
    const update = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      start = Math.floor(ease * target);
      setCount(start);
      if (progress < 1) requestAnimationFrame(update);
    };
    requestAnimationFrame(update);
  }, [active, target, duration]);
  return count;
}

function StatItem({ stat, active }: { stat: typeof stats[0]; active: boolean }) {
  const count = useCountUp(stat.value, active);
  return (
    <div data-component-id="src/app/homepage/components/StatsBanner.tsx:33:4" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="33" data-component-end-line="50" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22stat-card%20text-center%22%7D" className="stat-card text-center">
      <div data-component-id="src/app/homepage/components/StatsBanner.tsx:34:6" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="34" data-component-end-line="46" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-heading%20mb-2%22%7D"
        className="font-heading mb-2"
        style={{
          fontSize: 'clamp(36px, 5vw, 56px)',
          color: stat.color,
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 800,
          letterSpacing: '-0.04em',
          lineHeight: 1,
        }}
      >
        {count}{stat.suffix}
      </div>
      <div data-component-id="src/app/homepage/components/StatsBanner.tsx:47:6" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="47" data-component-end-line="49" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{ color: '#888888', fontSize: '13px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {stat.label}
      </div>
    </div>
  );
}

export default function StatsBanner() {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('active'), i * 100);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/homepage/components/StatsBanner.tsx:77:4" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="77" data-component-end-line="100" data-component-file="StatsBanner.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20md%3Apy-20%22%7D"
      ref={sectionRef}
      className="py-16 md:py-20"
      style={{
        background: '#000000',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div data-component-id="src/app/homepage/components/StatsBanner.tsx:86:6" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="86" data-component-end-line="99" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-container%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src/app/homepage/components/StatsBanner.tsx:87:8" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="87" data-component-end-line="91" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22reveal%20mb-10%20text-center%22%7D" className="reveal mb-10 text-center">
          <p data-component-id="src/app/homepage/components/StatsBanner.tsx:88:10" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="88" data-component-end-line="90" data-component-file="StatsBanner.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22Tehetna%20Diagnostic%20Garage%20%E2%80%94%20Trusted%20by%20Addis%20Ababa%20Drivers%22%7D" style={{ color: '#555555', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Tehetna Diagnostic Garage — Trusted by Addis Ababa Drivers
          </p>
        </div>
        <div data-component-id="src/app/homepage/components/StatsBanner.tsx:92:8" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="92" data-component-end-line="98" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20md%3Agrid-cols-4%20gap-px%22%7D" className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {stats.map((stat, i) => (
            <div data-component-id="src/app/homepage/components/StatsBanner.tsx:94:12" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="94" data-component-end-line="96" data-component-file="StatsBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={stat.label} className={`reveal delay-${(i + 1) * 100}`}>
              <StatItem data-component-id="src/app/homepage/components/StatsBanner.tsx:95:14" data-component-path="src/app/homepage/components/StatsBanner.tsx" data-component-line="95" data-component-end-line="95" data-component-file="StatsBanner.tsx" data-component-name="StatItem" data-component-content="%7B%22elementName%22%3A%22StatItem%22%7D" stat={stat} active={active} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}